import { MTLB } from './constants'

type Bag = { [key: string]: any }

const DOMAIN_MASK = 'xxx.xxx'
const HOST_MASK = `http://${DOMAIN_MASK}`

/**
 * This is similar to the `URL` class but it can handle relative URLs since
 * that's what we mostly work with.
 */
export class RelativeURL {
  private url: URL
  private actual: string
  private isHostMasked = false
  private _searchParams: ArrayURLSearchParams

  constructor(actual: string) {
    this.actual = actual

    try {
      this.url = new URL(actual)
    } catch (e) {
      this.isHostMasked = true
      this.url = new URL(actual, HOST_MASK)
    }

    this._searchParams = new ArrayURLSearchParams(
      // Passing a string resolves an issue where the URLSearchParams instance
      // created by the URL class in the Jest environment is not actually an
      // `instanceof URLSearchParams`.
      this.url.searchParams.toString(),
    )
  }

  /**
   * Whether the original URL includes a hostname.
   */
  // TODO Rename isAbsoluteURL
  get isFullURL() {
    return !this.isHostMasked && this.actual.includes('://')
  }

  /**
   * Whether the original URL is a relative to the root of the domain.
   */
  get isRootRelative() {
    return this.actual.startsWith('/')
  }

  get protocol() {
    return this.isFullURL ? `${this.url.protocol}//` : ''
  }

  get hostname() {
    // The URL class works with absolute URLs but we mostly work with
    // relative paths so we remove the domainMask if it was used in order
    // to get the URL() constructor to work.
    return this.url.hostname.replace(DOMAIN_MASK, '')
  }

  get pathname() {
    // The URL class works with absolute URLs but we mostly work with
    // relative paths so this function needs to be able to handle both.
    // For that reason, we create the pathname by removing the domain,
    // hash and search params from the original string we were passed.
    // This should work for absolute URLs, relative to the current page
    // URLs and relative to the root URLs.
    if (this.isFullURL) return this.url.pathname
    else
      return this.actual
        .replace(this.url.origin, '')
        .split('?')
        .at(0)
        .split('#')
        .at(0)
  }

  get search() {
    return this._searchParams.toString()
  }

  get searchParams() {
    return this._searchParams
  }

  /**
   * Get the value of a search parameter from the URL. If the parameter is listed
   * multiple times, an array of values is returned.
   */
  getSearchParam(param: string) {
    const paramValue: string[] | string = this.url.searchParams.getAll(param)
    if (paramValue.length === 0) return undefined
    else if (paramValue.length === 1) return paramValue[0]
    else return paramValue
  }

  toString() {
    return this.protocol + this.hostname + this.pathname + this.search
  }
}

export type Stringable = string | string[] | boolean | number | null | undefined

/**
 * An object that can be used to reperesent the search parameters in a URL.
 */
export type GenericSearchParams = Record<string, Stringable>

export type SearchParams = string | URLSearchParams | GenericSearchParams

/**
 * Append the provided search parameters to the given URLSearchParams object.
 * `null` values will result in deleting the key from the URLSearchParams. Array
 * values will be appended as multiple values. All other values will be coerced to
 * strings.
 */
function appendSearchParams(obj: GenericSearchParams, params: URLSearchParams) {
  Object.entries(obj).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      params.delete(key)
      value.forEach((v) => params.append(key, String(v)))
    } else {
      if (value == null) {
        params.delete(key)
      } else {
        params.set(key, String(value))
      }
    }
  })
}

// TODO Testing
export class ArrayURLSearchParams {
  private params: URLSearchParams

  constructor(params: SearchParams) {
    if (typeof params === 'string') {
      this.params = new URLSearchParams(params)
    } else if (params instanceof URLSearchParams) {
      // TODO When running in the Jest environment, this may fail because the
      // `new URL().searchParams instanceof URLSearchParams` is false. We should
      // either submit a bug to Jest or find a workaround to use here.
      this.params = params
    } else if (typeof params === 'object') {
      this.params = new URLSearchParams()
      appendSearchParams(params, this.params)
    }
  }

  get(key: string) {
    const value = this.params.getAll(key)
    if (value.length === 0) return undefined
    else if (value.length === 1) return coerceStringToType(value[0])
    else return value.map((v) => coerceStringToType(v))
  }

  set(key: string, value: Stringable) {
    appendSearchParams({ [key]: value }, this.params)
  }

  /**
   * Returns the list of de-duplicated keys in sorted order.
   */
  keys() {
    return Array.from(new Set(Array.from(this.params.keys()))).sort()
  }

  toString() {
    // Usually you could just call `params.sort()` but that isn't working in our
    // Jest tests. So to get everything working consistently, we get the unique
    // keys from the search params, sort them and then create a new string manually.
    const s = this.keys()
      .map((key) =>
        this.params
          .getAll(key)
          .sort()
          .map(
            (v) =>
              `${key}=${encodeURIComponent(String(v)).replace('%20', '+')}`,
          )
          .join('&'),
      )
      .join('&')
    return !!s ? `?${s}` : ''
  }
}

export type ParamType = 'string' | 'number' | 'boolean'
export type ParamSchema = ParamType | readonly [ParamType]
export type ParamsSchema = Record<string, ParamSchema>

type TypeFor<T> = T extends 'string'
  ? string
  : T extends 'number'
    ? number
    : T extends 'boolean'
      ? boolean
      : never

type ResolveSchema<T> = T extends readonly [infer E]
  ? Array<TypeFor<E>> | null
  : TypeFor<T> | null

export type ParsedParams<S extends ParamsSchema> = {
  [K in keyof S]: ResolveSchema<S[K]>
} & Record<string, unknown>

/**
 * Coerce a string representing some other type into that type.  For example,
 * 'true' and 'false' will be converted to booleans. Anything that can be
 * converted to a Number, will be (unless the property name ends with "Id" such
 * as "propertyId"). Anything surrounded in '{}' will be JSON parsed. Anything
 * surrounded in '[]' will be converted into an array with its elements run
 * through this function.
 *
 * When `type` is provided, it forces the output type instead of relying on
 * inference: missing values become `null` (or `false` for `'boolean'`), and
 * non-numeric strings declared as `'number'` become `NaN`. `*Id`-suffixed keys
 * stay strings under inference but are coerced normally when declared in a
 * schema.
 */
export function coerceStringToType(
  value: string,
  key = '',
  type?: ParamType,
): unknown {
  // Nil — single rule for inference and schema paths.
  if (value == null || value === '') {
    if (type === 'boolean') return false
    if (type) return null
    return value
  }
  // Array literal — recurse with the same `type` so schema'd arrays coerce
  // each element to the declared element type.
  if (value.startsWith('[') && value.endsWith(']')) {
    try {
      return value
        .slice(1, -1)
        .split(',')
        .map((v) => coerceStringToType(v.trim(), key, type))
    } catch (e) {
      // Cannot use logger here because it will cause a circular dependency
      console.warn('Unable to parse array query parameter:', e)
      return value
    }
  }
  // Object literal
  // 'object' type, so fall through to the type-driven branches below.
  if (!type && value.startsWith('{') && value.endsWith('}')) {
    try {
      return JSON.parse(decodeURI(value).replace(/'/g, '"'))
    } catch (e) {
      // Cannot use logger here because it will cause a circular dependency
      console.warn('Unable to parse object query parameters:', e)
      return value
    }
  }
  // Quoted string — unwrap. Under inference, return verbatim (quotes are
  // the author's signal "leave this as a string"). Under a schema, still
  // coerce the inner value (e.g. `{ count: 'number' }` on `"42"` → 42).
  if (
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'"))
  ) {
    const inner = value.slice(1, -1)
    return type ? coerceStringToType(inner, key, type) : inner
  }
  // Booleans — same strict rule for both paths: only the literal strings
  // 'true'/'false' map to booleans; anything else under `type === 'boolean'`
  // falls through to `false`.
  if (
    type === 'boolean' ||
    (!type && (value === 'true' || value === 'false'))
  ) {
    if (value === 'true') return true
    if (value === 'false') return false
    return false
  }
  // Numbers — schema forces numeric coercion (yielding NaN for non-numeric);
  // inference only coerces parsable numbers on non-`*Id` keys.
  if (
    type === 'number' ||
    (!type && !isNaN(Number(value)) && !key.endsWith('Id'))
  ) {
    return Number(value)
  }
  // String (explicit) or fallthrough (inference) — leave as-is.
  return value
}

/**
 * Parse an object of key value pairs where values should be coerced from
 * strings to other types. This is useful for converting query parameters from
 * string values into numbers, objects, arrays, booleans.
 *
 * Pass a `schema` to declare expected types for specific keys: `'string'`,
 * `'number'`, `'boolean'`, or `['string'|'number'|'boolean']` for arrays.
 * Schema'd keys bypass the default inference (e.g. the `*Id` rule) and the
 * return type narrows to match. Declared-but-missing keys come back as `null`
 * (or `false` for booleans). Keys not in the schema still go through inference.
 */
export function parseQueryParams(params: Bag): Record<string, unknown>
export function parseQueryParams<S extends ParamsSchema>(
  params: Bag,
  schema: S,
): ParsedParams<S>
export function parseQueryParams(
  params: Bag,
  schema?: ParamsSchema,
): Record<string, unknown> {
  if (!params) return {}

  const elementTypeOf = (decl: ParamSchema | undefined) =>
    Array.isArray(decl) ? decl[0] : (decl as ParamType | undefined)

  const out: Bag = Object.keys(params).reduce((acc, key) => {
    const value = params[key]
    const decl = schema?.[key]
    const type = elementTypeOf(decl)
    const coerced = Array.isArray(value)
      ? value.map((v) => coerceStringToType(v, key, type))
      : coerceStringToType(value, key, type)
    // Schema declares an array but URL had a single value — wrap so the
    // runtime shape matches the declared type (e.g. `{ tag: ['string'] }`
    // on `?tag=hello` → `['hello']`).
    acc[key] =
      Array.isArray(decl) && coerced != null && !Array.isArray(coerced)
        ? [coerced]
        : coerced
    return acc
  }, {} as Bag)

  if (schema) {
    for (const key in schema) {
      if (key in out) continue
      out[key] = elementTypeOf(schema[key]) === 'boolean' ? false : null
    }
  }

  return out
}

/**
 * Convert a `URLSearchParams` object into a plain JS object of key/values.
 */
export function urlSearchParamsToObject(params: URLSearchParams) {
  const out = Array.from(params).reduce((acc, [key, value]) => {
    // If the key already exists, then convert the value into an array
    if (acc[key]) {
      acc[key] = [].concat(acc[key], value)
    } else {
      acc[key] = value
    }
    return acc
  }, {})
  return out
}

/**
 * Create a `mailto:` link that works on all systems. While creating a `mailto:`
 * link seems pretty simple, there are a few subtleties when trying to add
 * multiple lines of text to the email body and getting that to work on web and
 * native.
 */
export function mailToLink(
  /**
   * The email you are sending to.
   */
  email: string,
  /**
   * The email subject.
   */
  subject?: string,
  /**
   * The email body text. If you pass an array, then each item is treated as a
   * separate line. You can add extra new lines by inserting empty strings (ex
   * `['foo', '', 'bar']` gives an empty line between "foo" and "bar").
   */
  body?: string | string[],
) {
  const params = [
    ['subject', subject],
    // Combine body lines into a single string
    ['body', Array.isArray(body) ? body.join(MTLB) : body],
  ]
    // Filter any parameters that don't have a value
    .filter((p) => !!p[1])
    // Join the inner string pairs with =
    .map((p) => p.join('='))
    // Join the parameters into a single string
    .join('&')

  return encodeURI(
    [`mailto:${email}`, params]
      // Filter out the parameters if there are none
      .filter((v) => !!v)
      // And combine into a single URL string
      .join('?'),
  )
}

/**
 * A function that gets a param to a url. It can handle full urls or just paths/params.
 */
export function getUrlParam(url: string, param: string) {
  if (!url || !param) return undefined

  // Check if the provided URL is a full URL or just a path with query
  const isFullUrl = url.startsWith('http://') || url.startsWith('https://')

  // Create a full URL using a base URL if it's just a path
  const urlObj = isFullUrl ? new URL(url) : new URL(url, 'http://temp.com')

  // Get the search params object from the URL
  const params = urlObj.searchParams

  // Get the specified parameter
  const value = params.get(param)
  return typeof value === 'string' ? value.replace('+', ' ') : value
}

/**
 * A function that adds a param to a url. It can handle full urls or just paths/params.
 */
export function addUrlParam(url: string, param: string, value: string) {
  // Check if the provided URL is a full URL or just a path with query
  const isFullUrl = url.includes('://')
  const isAbsoluteURL = url.startsWith('/')

  // Create a full URL using a base URL if it's just a path
  // const urlObj = isFullUrl ? new URL(url) : new URL(url, 'http://temp.com')
  const urlObj = new RelativeURL(url)

  // Get the search params object from the URL
  const params = urlObj.searchParams

  // Set the specified parameter
  params.set(param, value)

  // // Convert search params to an array and sort it alphabetically
  // const sortedParams = [...params.entries()].sort((a, b) =>
  //   a[0].localeCompare(b[0]),
  // )
  //
  // // Clear the search params
  // sortedParams.forEach(([key]) => params.delete(key))
  //
  // // Append the sorted params back to the search params object
  // sortedParams.forEach(([key, value]) => {
  //   params.append(key, value)
  // })

  return urlObj.toString()
  // Return the updated URL as a string
  if (isFullUrl) {
    return urlObj.toString()
  } else {
    // Return only the pathname and search if it was a path
    return (
      // TODO This does not account for URLs like 'foo/bar?baz=qux'
      (isAbsoluteURL ? urlObj.pathname : '') +
      (urlObj.search ? urlObj.search : '')
    )
  }
}

/**
 * A function that removes a param from a url. It can handle full urls or just paths/params.
 */
export function removeUrlParam(url: string, param: string) {
  // Check if the provided URL is a full URL or just a path with query
  const isFullUrl = url.includes('://')

  // Create a full URL using a base URL if it's just a path
  const urlObj = isFullUrl ? new URL(url) : new URL(url, 'http://temp.com')

  // Get the search params object from the URL
  const params = urlObj.searchParams

  // Delete the specified parameter
  params.delete(param)

  // Return the updated URL as a string
  if (isFullUrl) {
    return urlObj.toString()
  } else {
    // Return only the pathname and search if it was a path
    return urlObj.pathname + (urlObj.search ? urlObj.search : '')
  }
}
